import { isArray } from "@suse-o11y/is-array";
import { isPresent } from "@suse-o11y/is-present";
import { toKeys } from "@suse-o11y/to-keys";
import isEmpty from "lodash/isEmpty";
import pickBy from "lodash/pickBy";
import {
	type QueryParamConfig,
	decodeDelimitedArray,
	decodeEnum,
	decodeNumber,
	decodeObject,
	decodeString,
	encodeDelimitedArray,
	encodeNumber,
	encodeObject,
	encodeString,
} from "serialize-query-params";

import {
	Api,
	type SpanSortDirection,
	type SpanSortField,
	type SpanSortOption,
	type SpanStatusCode,
} from "../../common/openapi";
import { decodeUniqDelimitedArray, encodeUniqDelimitedArray } from "../../generic/queryParamUtils";

import type { TracesFilterStateSnapshot } from "./_traces-filter";

const KEY_VALUE_SEPARATOR = "--";
const ENTRY_SEPARATOR = "__";
const LIST_VALUE_SEPARATOR = ",";
const RANGE_VALUE_SEPARATOR = "_";

const filterBy: QueryParamConfig<TracesFilterStateSnapshot | undefined, TracesFilterStateSnapshot> = {
	encode: (snapshot) => {
		const durationFrom = encodeNumber(snapshot?.durationFrom);
		const durationTo = encodeNumber(snapshot?.durationTo);

		const obj = pickBy(
			{
				status: encodeString(snapshot?.status),
				spanName: encodeString(snapshot?.spanName),
				duration:
					// should support duration param in url if one of 'durationFrom' or 'durationTo' is defined
					durationFrom == null && durationTo == null
						? undefined
						: encodeDelimitedArray([durationFrom || null, durationTo || null], RANGE_VALUE_SEPARATOR),
				attributes: snapshot?.attributes
					? encodeUniqDelimitedArray(snapshot.attributes, LIST_VALUE_SEPARATOR)
					: undefined,
			},
			isPresent,
		);

		if (isEmpty(obj)) {
			return undefined;
		}

		return encodeObject(obj, KEY_VALUE_SEPARATOR, ENTRY_SEPARATOR);
	},

	decode: (valueString) => {
		const obj = decodeObject(valueString, KEY_VALUE_SEPARATOR, ENTRY_SEPARATOR);
		const [durationFrom, durationTo] = decodeUniqDelimitedArray(obj?.duration, RANGE_VALUE_SEPARATOR) ?? [];

		return {
			spanName: decodeString(obj?.spanName) ?? undefined,

			attributes: obj?.attributes === "" ? [] : decodeUniqDelimitedArray(obj?.attributes, LIST_VALUE_SEPARATOR),
			status: decodeEnum(obj?.status, toKeys(Api.SpanStatusCode) as Array<SpanStatusCode>) ?? undefined,
			durationFrom: decodeNumber(durationFrom) ?? undefined,
			durationTo: decodeNumber(durationTo) ?? undefined,
		};
	},
};

const sortBy: QueryParamConfig<undefined | Array<SpanSortOption>, Array<SpanSortOption>> = {
	encode: (value) => {
		if (value == null) {
			return undefined;
		}

		const list = (isArray(value) ? value : [value])
			.map(({ field, direction }) => encodeDelimitedArray([field, direction], KEY_VALUE_SEPARATOR))
			.filter(isPresent);

		if (isEmpty(list)) {
			return undefined;
		}

		return encodeDelimitedArray(list, LIST_VALUE_SEPARATOR);
	},

	decode: (valueString) => {
		const list = decodeDelimitedArray(valueString, LIST_VALUE_SEPARATOR);

		if (list == null) {
			return [];
		}

		return list
			.map((value) => {
				const tuple = decodeDelimitedArray(value, KEY_VALUE_SEPARATOR) ?? [];
				const field = decodeEnum(tuple.at(0), toKeys(Api.SpanSortField) as Array<SpanSortField>);
				const direction = decodeEnum(tuple.at(1), toKeys(Api.SpanSortDirection) as Array<SpanSortDirection>);

				if (field == null || direction == null) {
					return null;
				}

				return { field, direction };
			})
			.filter(isPresent);
	},
};

export const tracesExplorerSearchParams = {
	filterBy,
	sortBy,
};
